<script setup lang="ts">
/**
 * The customer's picture with the channel's logo as a small badge on its bottom-right corner — the conversation list
 * row (owner 27 ก.ย. 69: round profile photo, small Facebook logo overlapping its corner with a white ring, the name
 * on the right with no logo before it). The ring takes the colour behind it (`--ring`, set by the row for hover /
 * selected / quiet rows) so the badge reads on every row. The letter circle (no picture) gets the same badge.
 * Accessible name: the badge (ChannelBadge role="img", e.g. "LINE", "Facebook · คอมเมนต์"); the picture is decorative.
 */
import type { Channel, ConversationKind } from '../../api/types'
import Avatar from './Avatar.vue'
import ChannelBadge from './ChannelBadge.vue'

defineProps<{
  initial: string
  url?: string | null
  contactId?: string | null
  channel: Channel
  kind?: ConversationKind
}>()
</script>

<template>
  <span class="cha">
    <Avatar :initial="initial" :url="url" :contact-id="contactId" />
    <span class="cha-ch" :class="`ch-${channel}`"><ChannelBadge :channel="channel" :kind="kind" /></span>
  </span>
</template>

<style scoped>
.cha {
  position: relative;
  flex: none;
  display: inline-flex;
}
/* the logo sits on the circle's edge at the bottom-right, 16 px + a 2 px ring in the row's colour */
.cha-ch {
  position: absolute;
  right: -3px;
  bottom: -3px;
  display: inline-flex;
}
.cha-ch :deep(.chl) {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 0 0 2px var(--ring, #fff);
}
.cha-ch :deep(.chl-logo) {
  width: 16px;
  height: 16px;
}
/* LINE's logo is a rounded square, Instagram's glyph too: the ring follows the shape */
.cha-ch.ch-line :deep(.chl) {
  border-radius: 24%;
}
.cha-ch.ch-ig :deep(.chl) {
  border-radius: 30%;
}
.cha-ch.ch-ig :deep(.chl-logo) {
  width: 14px;
  height: 14px;
}
/* web chat: the globe on a soft disc */
.cha-ch.ch-web :deep(.chl) {
  background: #e3ebe7;
}
.cha-ch.ch-web :deep(.chl-web .ic) {
  width: 12px;
  height: 12px;
  stroke-width: 2;
  color: #3e4d46;
}
/* comment under a post/ad: the small speech bubble on the logo, scaled to the badge */
.cha-ch :deep(.chl-cmt) {
  right: -5px;
  bottom: -4px;
  width: 11px;
  height: 11px;
  box-shadow: 0 0 0 1.5px var(--ring, #fff), 0 0 0 2.5px #d5dfda;
}
.cha-ch :deep(.chl-cmt .ic) {
  width: 8px;
  height: 8px;
}
</style>
